import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FHIR from 'fhirclient';
import { smartReady } from './smartReady';
import { resolveRole } from './resolveRole';
import { getViewConfig } from './viewConfig';
import { useSessionStore } from '../stores/sessionStore';

/**
 * SmartCallback Component — TV-RV-04
 * Completes the SMART on FHIR OAuth2 authorization code exchange.
 *
 * After the EHR's authorization server redirects back with an auth code,
 * fhirclient's oauth2.ready() handles:
 *   1. Authorization code → access token exchange
 *   2. PKCE verification
 *   3. Token response parsing (access_token, patient, encounter, etc.)
 *   4. Returns an authenticated FHIR Client instance
 *
 * TV-RV-04: Role resolution now uses the central resolveRole() — KC roles from
 * the decoded access token are authoritative; ?role= is a tiebreaker only among
 * roles the user holds (spoof-resistant). The old fhirUser-prefix-only
 * resolveRole() function has been deleted.
 */
export function SmartCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const {
    setCurrentPatient,
    setCurrentEncounter,
    setUserRole,
    setTenantId,
    setSMARTContext,
  } = useSessionStore();
  // React StrictMode dev fires effects twice. fhirclient.oauth2.ready()
  // consumes the OAuth code on the first call; the second call retries
  // the token exchange and KC rejects it as "invalid_grant: Code not
  // valid" (the code was already redeemed).
  //
  // The useRef guard alone is NOT sufficient — it's scoped to a single
  // component instance. When fhirclient strips the `code` query param
  // from the URL via history.replaceState (post-successful exchange),
  // react-router-dom interprets that as a navigation and remounts
  // SmartCallback. The fresh instance gets a fresh ref → the guard
  // doesn't fire → second exchange attempt → KC 400.
  //
  // Robust guard: BEFORE attempting the exchange, peek at fhirclient's
  // own sessionStorage state. If the SMART session already holds an
  // access token, the exchange already succeeded — just hydrate from
  // session storage and navigate without retrying.
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    if (exchangeStartedRef.current) {
      console.log('[SMART Callback] exchange already in flight (StrictMode double-mount) — skipping');
      return;
    }
    exchangeStartedRef.current = true;

    const completeAuth = async () => {
      try {
        // Hard guard: track the consumed `code` value in sessionStorage.
        // Survives component remounts. If the same code shows up twice,
        // we know the first exchange already happened — skip the second
        // attempt and rehydrate from fhirclient's session.
        const params = new URLSearchParams(window.location.search);
        const oauthCode = params.get('code');
        const consumedKey = oauthCode ? `smart.consumedCode.${oauthCode}` : null;
        const alreadyConsumed =
          consumedKey && sessionStorage.getItem(consumedKey) === '1';

        if (alreadyConsumed) {
          console.log(
            '[SMART Callback] code already consumed in this session — ' +
              'returning early; the original mount will navigate when it ' +
              'completes',
          );
          return;
        }
        if (consumedKey) {
          // Mark this code as consumed BEFORE the exchange so even if
          // the network call races with a remount, the second attempt
          // sees the flag.
          sessionStorage.setItem(consumedKey, '1');
        }

        // fhirclient handles the entire token exchange:
        //   - Reads code + state from URL
        //   - Verifies state matches what was stored during authorize()
        //   - Exchanges code for token (with PKCE verifier)
        //   - Parses the token response for launch context
        //   - Returns an authenticated Client instance
        // If sessionStorage already has a tokenResponse for this state,
        // fhirclient skips the network exchange and returns the cached
        // client. The `alreadyConsumed` flag above is a belt-and-braces
        // additional check.
        const client = await smartReady();

        // Extract launch context from the token response
        // The SMART spec defines these standard context fields:
        //   - patient: Patient resource ID (e.g., "12345")
        //   - encounter: Encounter resource ID (e.g., "enc-789")
        //   - fhirUser: FHIR reference to the user (e.g., "Practitioner/dr-001")
        const patientId = client.patient?.id || null;
        const encounterId = client.encounter?.id || null;
        const fhirUser = client.getFhirUser?.() || client.state?.tokenResponse?.fhirUser || '';
        const fhirServerUrl = client.state?.serverUrl || '';

        // TV-RV-04 — Decode the access token to extract KC roles.
        // KC SMART SPI embeds realm_access + resource_access claims in the
        // access token (requires lightweight-access-tokens = OFF for the
        // televisit client — TV-RV-03 realm config step).
        const accessToken = client.state?.tokenResponse?.access_token || '';
        const atClaims = decodeJwt(accessToken);

        // TV-RV-04 — Central resolver: KC roles authoritative, ?role= hint is
        // a tiebreaker only among the roles the user actually holds.
        // The callback URL params (state, code, …) do NOT carry the original
        // launch query string, so SmartLaunch persists the hint to
        // sessionStorage ('tvRoleHint') before the OAuth redirect. Prefer that;
        // fall back to a callback-URL ?role= for any launcher that forwards it.
        const launchHint = sessionStorage.getItem('tvRoleHint') || params.get('role') || '';
        sessionStorage.removeItem('tvRoleHint'); // one-shot; don't leak into next launch
        const role = resolveRole({ fhirUser, kcClaims: atClaims, launchHint });

        // Extract tenant ID from the FHIR server URL
        // e.g., https://r4.collabkare.io/tenant-123/ → "tenant-123"
        const tenantId = extractTenantId(fhirServerUrl);

        // Populate session store so the rest of the app has context
        if (patientId) setCurrentPatient(patientId);
        if (encounterId) setCurrentEncounter(encounterId);
        setUserRole(role);
        setTenantId(tenantId);
        setSMARTContext({
          patientId,
          encounterId,
          fhirServer: fhirServerUrl,
          fhirUser,
          authMode: 'ehr',
        });

        // Mark that we completed an EHR launch (AuthProvider reads this)
        sessionStorage.setItem('authMode', 'ehr');

        // TV-RV-07 — Role-aware redirect using VIEW_CONFIG
        const cfg = getViewConfig(role);
        console.log(`[SMART Callback] resolved role=${role} → navigating to ${cfg.home}`);
        navigate(cfg.home, { replace: true });
      } catch (err) {
        console.error('[SMART Callback] Token exchange failed:', err);
        setError(err.message);
      }
    };

    completeAuth();
  }, [navigate, setCurrentPatient, setCurrentEncounter, setUserRole, setTenantId, setSMARTContext]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface-primary">
        <div className="bg-surface-card border border-border rounded-xl p-8 max-w-md text-center">
          <div className="text-red-400 text-4xl mb-4">!</div>
          <h2 className="text-xl font-semibold text-white mb-2">Authorization Failed</h2>
          <p className="text-gray-400 text-sm mb-4">{error}</p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-500"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-surface-primary">
      <div className="text-center">
        <div className="inline-block">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
        <h2 className="mt-4 text-xl font-semibold text-white">Completing Authorization</h2>
        <p className="mt-2 text-sm text-gray-400">Setting up your session...</p>
      </div>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Decode the payload of a JWT without verifying the signature.
 * Used to extract KC role claims from the access token.
 */
function decodeJwt(jwt) {
  try {
    const seg = jwt?.split('.')?.[1];
    if (!seg) return {};
    const padded = seg + '='.repeat((4 - (seg.length % 4)) % 4);
    return JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
}

/**
 * Extracts tenant ID from a FHIR server URL.
 * Handles: https://r4.collabkare.io/tenant-123/
 *
 * @param {string} fhirServerUrl
 * @returns {string}
 */
function extractTenantId(fhirServerUrl) {
  if (!fhirServerUrl) return import.meta.env.VITE_DEFAULT_TENANT_ID || 'default';
  const match = fhirServerUrl.match(/r4\.collabkare\.io\/([a-zA-Z0-9_-]+)/);
  if (match) return match[1];
  // Generic: take last path segment
  const url = new URL(fhirServerUrl);
  const segments = url.pathname.split('/').filter(Boolean);
  return segments.length > 0 ? segments[segments.length - 1] : 'default';
}
